import React, {useEffect, useMemo, useState} from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

import {colors} from "nxtconstants";
import {RangeSlider} from "components/ClientHome/CalculatorComponents";
import {
  calculateSipSwp,
  DEFAULT_INPUT,
  formatCompactCurrency,
  formatSustainableAge,
  INPUT_LIMITS,
  MAX_SIMULATION_AGE,
  snapToStep
} from "./CalculatorLogic";
import {
  ChartHoverInfo,
  ChartLabel,
  getPhaseColor,
  MobileTabToggle,
  StackedInput,
  SummaryCards,
  ViewToggle,
  YearlyTable
} from "./CalculatorComponents";
import {
  CalculatorSection,
  CalculatorWrapper,
  ChartContainer,
  ControlPanel,
  FormGrid,
  FormGridFullWidth,
  PageTitle,
  PhaseSection,
  PhaseTitle,
  ResultsPanel
} from "./CalculatorStyle";

const CHART_AGE_INTERVAL = 1;

const normalizeChartPhase = (phase) => {
  if (phase === "HOLD") {
    return "HOLD";
  }

  if (phase === "SWP") {
    return "SWP";
  }

  return "SIP";
};

const buildChartData = (returnData, sustainTillAge) => {
  const mapped = returnData.map((row) => ({
    label: row.yearLabel,
    Corpus: row.Corpus,
    phase: normalizeChartPhase(row.phase),
    age: Number(row.yearLabel)
  }));

  if (!mapped.length) {
    return mapped;
  }

  const startAge = mapped[0].age;

  return mapped.filter((row, index) => {
    const ageOffset = row.age - startAge;

    return (
      index === 0 ||
      index === mapped.length - 1 ||
      ageOffset % CHART_AGE_INTERVAL === 0 ||
      row.age === sustainTillAge
    );
  });
};

const renderAgeTick = (sustainTillAge) =>
  function AgeTick({x, y, payload}) {
    const age = Number(payload.value);
    const showMarker = sustainTillAge < MAX_SIMULATION_AGE && age === sustainTillAge;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          dy={16}
          fill="#9ca3af"
          fontSize={12}
          textAnchor="middle"
          x={0}
          y={0}
        >
          {payload.value}
        </text>
        {showMarker ? (
          <circle cx={0} cy={28} fill={colors.chartSustainMarker} r={4} />
        ) : null}
      </g>
    );
  };

const SipSwpCalculator = () => {
  const [currentAge, setCurrentAge] = useState(DEFAULT_INPUT.currentAge);
  const [sipEndAge, setSipEndAge] = useState(DEFAULT_INPUT.sipEndAge);
  const [sipAmount, setSipAmount] = useState(DEFAULT_INPUT.sipAmount);
  const [returnRate, setReturnRate] = useState(DEFAULT_INPUT.returnRate);
  const [stepUp, setStepUp] = useState(DEFAULT_INPUT.stepUp);
  const [lumpsumAmount, setLumpsumAmount] = useState(DEFAULT_INPUT.lumpsumAmount);
  const [swpStartAge, setSwpStartAge] = useState(DEFAULT_INPUT.swpStartAge);
  const [monthlyWithdrawal, setMonthlyWithdrawal] = useState(DEFAULT_INPUT.monthlyWithdrawal);
  const [yearlyIncrease, setYearlyIncrease] = useState(DEFAULT_INPUT.yearlyIncrease);
  const [withdrawReturnRate, setWithdrawReturnRate] = useState(
    DEFAULT_INPUT.withdrawReturnRate
  );
  const [activeView, setActiveView] = useState("graph");
  const [mobileTab, setMobileTab] = useState("input");
  const [chartKey, setChartKey] = useState(0);

  useEffect(() => {
    let timeoutId;

    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setChartKey((previous) => previous + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  const result = useMemo(
    () =>
      calculateSipSwp({
        currentAge,
        sipEndAge,
        sipAmount,
        returnRate,
        stepUp,
        lumpsumAmount,
        swpStartAge,
        monthlyWithdrawal,
        yearlyIncrease,
        withdrawReturnRate
      }),
    [
      currentAge,
      sipEndAge,
      sipAmount,
      returnRate,
      stepUp,
      lumpsumAmount,
      swpStartAge,
      monthlyWithdrawal,
      yearlyIncrease,
      withdrawReturnRate
    ]
  );

  const chartData = useMemo(
    () => buildChartData(result.returnData, result.sustainTillAge),
    [result.returnData, result.sustainTillAge]
  );

  const sliderProps = {
    accentColor: colors.primary,
    showRangeLabels: false
  };

  return (
    <CalculatorSection>
      <PageTitle>SIP + SWP Calculator</PageTitle>

      <MobileTabToggle activeTab={mobileTab} onChange={setMobileTab} />

      <CalculatorWrapper>
        <ControlPanel $hidden={mobileTab === "result"}>
          <PhaseSection>
            <FormGrid>
              <FormGridFullWidth>
                <PhaseTitle>SIP Phase</PhaseTitle>
              </FormGridFullWidth>

              <StackedInput
                label="Current Age"
                value={currentAge}
                onChange={setCurrentAge}
                min={INPUT_LIMITS.currentAge.min}
                max={INPUT_LIMITS.currentAge.max}
                step={INPUT_LIMITS.currentAge.step}
                suffix=" Years"
              />
              <StackedInput
                label="SIP End Age"
                value={sipEndAge}
                onChange={setSipEndAge}
                min={INPUT_LIMITS.sipEndAge.min}
                max={INPUT_LIMITS.sipEndAge.max}
                step={INPUT_LIMITS.sipEndAge.step}
                suffix=" Years"
              />

              <FormGridFullWidth>
                <RangeSlider
                  label="Monthly SIP Amount"
                  value={sipAmount}
                  onChange={(value) =>
                    setSipAmount(snapToStep(value, INPUT_LIMITS.sipAmount.step))
                  }
                  min={INPUT_LIMITS.sipAmount.min}
                  max={INPUT_LIMITS.sipAmount.max}
                  step={INPUT_LIMITS.sipAmount.step}
                  prefix="₹"
                  {...sliderProps}
                />
              </FormGridFullWidth>

              <StackedInput
                label="Annual Return (%)"
                value={returnRate}
                onChange={setReturnRate}
                min={INPUT_LIMITS.returnRate.min}
                max={INPUT_LIMITS.returnRate.max}
                step={INPUT_LIMITS.returnRate.step}
                suffix="%"
              />
              <StackedInput
                label="Annual Step-Up (%)"
                value={stepUp}
                onChange={setStepUp}
                min={INPUT_LIMITS.stepUp.min}
                max={INPUT_LIMITS.stepUp.max}
                step={INPUT_LIMITS.stepUp.step}
                suffix="%"
              />

              <FormGridFullWidth>
                <RangeSlider
                  label="Lumpsum Amount"
                  value={lumpsumAmount}
                  onChange={(value) =>
                    setLumpsumAmount(snapToStep(value, INPUT_LIMITS.lumpsumAmount.step))
                  }
                  min={INPUT_LIMITS.lumpsumAmount.min}
                  max={INPUT_LIMITS.lumpsumAmount.max}
                  step={INPUT_LIMITS.lumpsumAmount.step}
                  prefix="₹"
                  {...sliderProps}
                />
              </FormGridFullWidth>
            </FormGrid>
          </PhaseSection>

          <PhaseSection>
            <FormGrid>
              <FormGridFullWidth>
                <PhaseTitle>SWP Phase</PhaseTitle>
              </FormGridFullWidth>

              <FormGridFullWidth>
                <RangeSlider
                  label="SWP Start Age"
                  value={swpStartAge}
                  onChange={setSwpStartAge}
                  min={INPUT_LIMITS.swpStartAge.min}
                  max={INPUT_LIMITS.swpStartAge.max}
                  step={INPUT_LIMITS.swpStartAge.step}
                  suffix=" Years"
                  {...sliderProps}
                />
              </FormGridFullWidth>

              <FormGridFullWidth>
                <RangeSlider
                  label="Monthly Withdrawal"
                  value={monthlyWithdrawal}
                  onChange={(value) =>
                    setMonthlyWithdrawal(
                      snapToStep(value, INPUT_LIMITS.monthlyWithdrawal.step)
                    )
                  }
                  min={INPUT_LIMITS.monthlyWithdrawal.min}
                  max={INPUT_LIMITS.monthlyWithdrawal.max}
                  step={INPUT_LIMITS.monthlyWithdrawal.step}
                  prefix="₹"
                  {...sliderProps}
                />
              </FormGridFullWidth>

              <StackedInput
                label="Yearly Increase (%)"
                value={yearlyIncrease}
                onChange={setYearlyIncrease}
                min={INPUT_LIMITS.yearlyIncrease.min}
                max={INPUT_LIMITS.yearlyIncrease.max}
                step={INPUT_LIMITS.yearlyIncrease.step}
                suffix="%"
              />
              <StackedInput
                label="Return During Withdrawal (%)"
                value={withdrawReturnRate}
                onChange={setWithdrawReturnRate}
                min={INPUT_LIMITS.withdrawReturnRate.min}
                max={INPUT_LIMITS.withdrawReturnRate.max}
                step={INPUT_LIMITS.withdrawReturnRate.step}
                suffix="%"
              />
            </FormGrid>
          </PhaseSection>
        </ControlPanel>

        <ResultsPanel $hidden={mobileTab === "input"}>
          <SummaryCards
            corpusBeforeSwp={result.corpusBeforeSwp}
            corpusUsed={result.corpusUsed}
            leftoverCorpus={result.leftoverCorpus}
            sustainTillAge={result.sustainTillAge}
            swpStartAge={swpStartAge}
          />

          <ViewToggle activeView={activeView} onChange={setActiveView} />

          {activeView === "graph" ? (
              <ChartContainer>
                <ResponsiveContainer width="100%" height={440} key={chartKey}>
                  <BarChart
                    barCategoryGap="50%"
                    barGap={3}
                    barSize={5}
                    data={chartData}
                    margin={{top: 10, right: 5, left: 5, bottom: 30}}
                  >
                    <CartesianGrid
                      stroke="#f1f5f9"
                      strokeDasharray="3 3"
                      vertical={false}
                    />
                    <XAxis
                      axisLine={false}
                      dataKey="label"
                      tick={renderAgeTick(result.sustainTillAge)}
                      tickLine={false}
                    />
                    <YAxis
                      axisLine={false}
                      domain={[0, "dataMax"]}
                      tick={{fontSize: 12, fill: "#9ca3af"}}
                      tickCount={5}
                      tickFormatter={formatCompactCurrency}
                      tickLine={false}
                    />
                    <Tooltip content={<ChartHoverInfo />} cursor={colors.chartCursor} />
                    <Legend
                      align="center"
                      content={<ChartLabel />}
                      layout="horizontal"
                      verticalAlign="bottom"
                    />
                    <Bar
                      dataKey="Corpus"
                      name="Corpus"
                      radius={[3, 3, 0, 0]}
                    >
                      {chartData.map((entry) => (
                        <Cell fill={getPhaseColor(entry.phase)} key={entry.label} />
                      ))}
                    </Bar>
                  </BarChart>
              </ResponsiveContainer>
            </ChartContainer>
          ) : (
            <YearlyTable yearlyData={result.returnData} />
          )}
        </ResultsPanel>
      </CalculatorWrapper>
    </CalculatorSection>
  );
};

export default SipSwpCalculator;
